import React from 'react';
import { toast } from 'react-toastify';

import authService from '@/services/AuthService';
import { publish, subscribe } from '@/services/utils/pubsub';
import { events } from '@/services/wss/SystemEvents';
import logService from '@/services/wss/logService3';
import messageFactory from '@/services/wss/MessageFactory';
import systemStreamer from '@/services/wss/SystemStreamer';
import quotaEngine from '@/services/engines/QuotaEngine';
import userStateEngine2 from '@/controllers/UserState/UserStateEngine2';
import AppContext from '@/controllers/AppContext';
import { StreamEvents } from '@/services/wss/StreamEvents';
import userSettingsEngine from '@/controllers/UserSettings/UserSettingsEngine';
import { createSRHandlers } from './PageContentSRHandlers';
import { delay } from 'lodash';

import ComponentNotifier from '@/services/wss/ComponentNotifier';
import registerDialogs from '@/JxControls/JxWindowManager/registerWindowManagerDialogs';
import CoreComponent from '@/services/engines/CoreComponent';
import wssEventNotifier from '@/services/wss/WssEventNotifier';
import { bootEngines, bootFetchers, unmountEngines } from './BootRegistry';

const handlers = createSRHandlers();

let _name = 'PageContent';

class PageContent extends CoreComponent {
    componentDidMount = async () => {
        if (this.mounted) return;
        this.mounted = true;

        this.onDataReceived = subscribe(events.onSocketDataReceived, (p) => this.handleData(p));
        this.onReconnected = subscribe(events.onReconnected, (p) => this.handleReconnected(p));
        this.onTabChanged = ComponentNotifier.subscribe('TabEngine', () => this.forceUpdate());

        try {
            await AppContext.init();

            // No auth needed — pure local defaults fill. Must run before checkSignalR's
            // fetchSettings() Promise.all, otherwise initDefaults() wipes the BE overrides.
            userSettingsEngine.bootup();

            const token = await window.Clerk?.session?.getToken({ skipCache: true });
            if (!token) {
                this.logError('ComponentDidMount', 'Clerk token is null — aborting bootup');
                return;
            }

            this.logDebug('ComponentDidMount', `Clerk token is available - ${token}`);

            const doReconnect = await this.checkSignalR(token);
            if (doReconnect) {
                const isInitialized = await this.checkSignalR();
                if (!isInitialized) {
                    await this.handleReconnected();
                }
            }

            // Generic, auth-independent bootups — order-independent, see BootRegistry.js
            bootEngines.forEach((engine) => engine.bootup());

            registerDialogs();

            if (import.meta.env.DEV && userStateEngine2.isEmpty()) {
                logService.logError(`${_name}.mounted: Reconnect due to shit vite`);
                await this.handleReconnected();
            }
        } catch (error) {
            logService.logError(`${_name}.mounted: ${error.message}`);
        }
    };

    componentWillUnmount = async () => {
        if (!this.mounted) return;
        this.mounted = false;

        if (this.onDataReceived) this.onDataReceived = this.onDataReceived();
        if (this.onReconnected) this.onReconnected = this.onReconnected();
        if (this.onTabChanged) this.onTabChanged = this.onTabChanged();

        unmountEngines.forEach((engine) => engine.clear());
        AppContext.destroy();

        const token = await window?.Clerk?.session?.getToken();
        if (token === undefined) return;

        await authService.cleanup(token);
        delay(() => {}, 500);
    };

    checkSignalR = async (token) => {
        try {
            const isInitialized = await authService.initStreamer(token);

            const tabId = AppContext.getTabId();
            const payload = messageFactory.createRequest(StreamEvents.onUserAuthenticated, token, tabId);
            const msg = messageFactory.serialize(payload);
            const send = await systemStreamer.send(msg);
            if (send) {
                // user is logged on AND we have a live signalR connection !
                // Fetches all user/tab-dependent data — see BootRegistry.js
                await Promise.all(bootFetchers.map((fn) => fn()));

                return false;
            }

            logService.logError(`${_name}.checkSignalR: message not send `);
            const r = await authService.cleanup();
            return r;
        } catch (error) {
            logService.logError(`${_name}.checkSignalR: ex=${error.message}`);
        }
    };

    handleReconnected = async (p) => {
        try {
            const token = await window.Clerk.session.getToken();
            if (token === undefined) return;

            const tabId = AppContext.getTabId();
            const payload = messageFactory.createRequest(StreamEvents.onUserReconnected, token, tabId);
            const msg = messageFactory.serialize(payload);
            const send = await systemStreamer.send(msg);
            if (send) {
                return;
            }

            await authService.cleanup();

            logService.logError(`${_name}.checkSignalR: message not send `);
        } catch (error) {
            logService.logError(`${_name}.handleReconnected: ex=${error.message}`);
        }
    };

    handleData = (p) => {
        const handler = handlers[p.A];
        if (handler) {
            handler.handle(JSON.parse(p.D));
            return;
        }

        wssEventNotifier.notify(p.A, JSON.parse(p.D));

        if (p.A === 'OnNotificationPublished') {
            const item = JSON.parse(p.D);
            toast(item.message, { position: 'bottom-right', className: 'toast-message' });
            return;
        }

        if (p.A === 'OnJournalDeleted') {
            const items = JSON.parse(p.D);
            items.forEach((item) => publish(events.onNoteDeleted, item.rk));
        }

        if (p.A === 'OnQuotaUpdated' || p.A === events.onQuotaDeleted) {
            quotaEngine.handleSocketData(p);
        }
    };

    render() {
        console.log('PageContent rendering');
        return <>{this.props.children}</>;
    }
}

export default PageContent;
